import { useState, useRef, useCallback, useEffect } from 'react';
import { tokenManager } from '../services/api';

/**
 * SSE hook for canvassing scrape progress — follows useImportStream pattern
 */
const useScrapeStream = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [validationProgress, setValidationProgress] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [completedStats, setCompletedStats] = useState(null);

  const eventSourceRef = useRef(null);
  const completedRef = useRef(false);

  /**
   * Connect to the scrape progress SSE stream.
   * Returns a Promise that resolves when the connection is established.
   */
  const connectToScrapeStream = useCallback((jobId) => {
    return new Promise((resolve) => {
      // Disconnect any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const token = tokenManager.getToken();
      if (!token) {
        setConnectionError('No authentication token');
        resolve();
        return;
      }

      // Build SSE URL
      let baseUrl = process.env.REACT_APP_API_URL || '';
      if (baseUrl.endsWith('/api')) baseUrl = baseUrl.slice(0, -4);
      if (!baseUrl.includes('/api')) baseUrl += '/api';

      const url = `${baseUrl}/canvassing/scrape/stream/${jobId}?token=${encodeURIComponent(token)}`;

      let resolved = false;
      const doResolve = () => {
        if (!resolved) { resolved = true; resolve(); }
      };

      try {
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;
        completedRef.current = false;
        setIsCompleted(false);
        setHasError(false);
        setCompletedStats(null);

        // Timeout fallback — resolve anyway after 3s so we don't block forever
        setTimeout(doResolve, 3000);

        eventSource.onopen = () => {
          setIsConnected(true);
          setConnectionError(null);
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            switch (data.type) {
              case 'connected':
                setIsConnected(true);
                setScrapeStatus('connected');
                doResolve();
                break;

              case 'heartbeat':
                break;

              case 'progress':
                // Merge with previous progress so fields aren't lost on phase transitions
                setProgress(prev => prev ? { ...prev, ...data } : data);
                setScrapeStatus(data.phase === 'validating' ? 'validating' : 'scraping');
                break;

              case 'validation_progress':
                setValidationProgress(data);
                // Only switch to validating status if we have real scrape progress
                // (prevents standalone revalidation from hijacking extraction UI)
                setProgress(prev => {
                  if (prev && prev.totalPhrases > 0) {
                    setScrapeStatus('validating');
                    return { ...prev, phase: 'validating' };
                  }
                  // No extraction running — just update validation progress silently
                  return prev;
                });
                break;

              case 'completed':
                // Only the orchestrator sends 'completed' — this is a real extraction finish
                completedRef.current = true;
                setIsCompleted(true);
                setCompletedStats(data.stats || data);
                setScrapeStatus('completed');
                setTimeout(() => disconnectFromScrapeStream(), 2000);
                break;

              case 'validation_completed':
                // Standalone validation for a specific job — treat as extraction completion
                // only if we were tracking this job's extraction
                setProgress(prev => {
                  if (prev && prev.totalPhrases > 0) {
                    completedRef.current = true;
                    setIsCompleted(true);
                    setCompletedStats(data.stats || data);
                    setScrapeStatus('completed');
                    setTimeout(() => disconnectFromScrapeStream(), 2000);
                  }
                  return prev;
                });
                break;

              case 'revalidation_completed':
                // Standalone revalidation — never marks extraction as complete
                // Just update validation stats silently
                setValidationProgress(prev => prev ? { ...prev, completed: true } : prev);
                break;

              case 'error':
                setHasError(true);
                setScrapeStatus('error');
                setConnectionError(data.message);
                break;

              default:
                break;
            }
          } catch (parseError) {
            console.error('SSE parse error:', parseError);
          }
        };

        eventSource.onerror = () => {
          if (!completedRef.current) {
            setConnectionError('Connection lost');
            setIsConnected(false);
            doResolve();
            setTimeout(() => {
              if (!completedRef.current && eventSourceRef.current) {
                connectToScrapeStream(jobId);
              }
            }, 3000);
          }
        };
      } catch (error) {
        setConnectionError(error.message);
        doResolve();
      }
    });
  }, []);

  /**
   * Disconnect from the SSE stream
   */
  const disconnectFromScrapeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  /**
   * Reset all state
   */
  const resetScrapeStream = useCallback(() => {
    disconnectFromScrapeStream();
    setProgress(null);
    setValidationProgress(null);
    setScrapeStatus(null);
    setConnectionError(null);
    setIsCompleted(false);
    setHasError(false);
    setCompletedStats(null);
    completedRef.current = false;
  }, [disconnectFromScrapeStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => disconnectFromScrapeStream();
  }, [disconnectFromScrapeStream]);

  return {
    isConnected,
    connectionError,
    scrapeStatus,
    progress,
    validationProgress,
    isCompleted,
    hasError,
    completedStats,
    connectToScrapeStream,
    disconnectFromScrapeStream,
    resetScrapeStream
  };
};

export default useScrapeStream;
